/**
 * Settings store, persisted in the flat Codable-shaped format from
 * packages/schema/persist.ts (same pattern as progress.ts — see
 * ./persistedState.ts for how the two stores share one localStorage record).
 */

import type { PersistedSettings } from "@schema/persist.ts";
import { DEFAULT_SETTINGS } from "@schema/persist.ts";
import { create } from "zustand";
import { type PersistStorage, persist } from "zustand/middleware";
import { readPersistedState, writeSettings } from "./persistedState.ts";

/** What drives the phase theme. Re-exported here since it's a settings concept, not a catalog one. */
export type ThemeSource = PersistedSettings["themeSource"];

interface SettingsState {
  themeSource: ThemeSource;
  encoreMode: boolean;
  hoursPerWeek: number;
  alternativesEnabled: boolean;
  /**
   * Show the project's own rating on entry cards.
   *
   * Off is a legitimate way to read this guide: the tiers say what the finale
   * needs, which is fact-shaped, while the rating is one person's taste. A
   * reader who wants the route without the opinion can have it.
   */
  showRating: boolean;
  setThemeSource: (source: ThemeSource) => void;
  setEncoreMode: (on: boolean) => void;
  setHoursPerWeek: (hours: number) => void;
  setAlternativesEnabled: (on: boolean) => void;
  setShowRating: (on: boolean) => void;
}

type PersistedSettingsSlice = Pick<
  SettingsState,
  "themeSource" | "encoreMode" | "hoursPerWeek" | "alternativesEnabled" | "showRating"
>;

const toSlice = (settings: PersistedSettings): PersistedSettingsSlice => ({
  themeSource: settings.themeSource,
  encoreMode: settings.encoreMode,
  hoursPerWeek: settings.hoursPerWeek,
  alternativesEnabled: settings.alternativesEnabled,
  showRating: settings.showRating,
});

const storage: PersistStorage<PersistedSettingsSlice> = {
  getItem: (_name) => {
    const settings = readPersistedState().settings;
    return { state: toSlice(settings), version: 1 };
  },
  setItem: (_name, value) => {
    writeSettings({
      themeSource: value.state.themeSource,
      encoreMode: value.state.encoreMode,
      hoursPerWeek: value.state.hoursPerWeek,
      alternativesEnabled: value.state.alternativesEnabled,
      showRating: value.state.showRating,
    });
  },
  removeItem: () => {
    writeSettings(DEFAULT_SETTINGS);
  },
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...toSlice(DEFAULT_SETTINGS),
      setThemeSource: (themeSource) => set({ themeSource }),
      setEncoreMode: (encoreMode) => set({ encoreMode }),
      setHoursPerWeek: (hoursPerWeek) => set({ hoursPerWeek }),
      setAlternativesEnabled: (alternativesEnabled) => set({ alternativesEnabled }),
      setShowRating: (showRating) => set({ showRating }),
    }),
    {
      name: "settings", // unused as a real localStorage key — storage reads/writes the unified record directly
      version: 1,
      storage,
      partialize: ({ themeSource, encoreMode, hoursPerWeek, alternativesEnabled, showRating }) => ({
        themeSource,
        encoreMode,
        hoursPerWeek,
        alternativesEnabled,
        showRating,
      }),
    },
  ),
);
